/**
 * THE TOOLKIT FRONT DOOR — crm.narratoria.com/
 * ─────────────────────────────────────────────
 * World 1. This is where www.narratoria.com (Squarespace) sends people.
 *
 *   signed out            → Narratoria sign-in (Google primary) + "New here?"
 *   signed in, licensed   → the three-card hub
 *   signed in, no licence → the hub, whose cards show their own locked state
 *
 * NO ARC OR READER SURFACE APPEARS HERE, for anyone — including superadmin.
 * ARC lives behind its own door at /author/login and is never linked from here.
 *
 * Superadmin is redirected to /admin on SIGN-IN ONLY, never by a guard: a guard
 * on this route would bounce Julie away from the hub every time she tried to
 * use her own licence.
 */
import React, { useState, useEffect } from 'react';
import { auth, googleProvider } from '../firebase';
import { signInWithPopup, signInWithEmailAndPassword, sendEmailVerification, getIdTokenResult } from 'firebase/auth';
import { useAuth } from '../context/AuthContext';
import useDocumentTitle from '../lib/useDocumentTitle';
import AuthorHub from '../hub/AuthorHub';
import * as T from '../brand/tokens';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { BookOpen, Mail, ArrowRight, Loader2, LogOut, ChevronDown, MailWarning, Check } from 'lucide-react';

/** Where a role lands immediately after signing in AT THE ROOT. */
export function landingForRoles(roles = []) {
  // Superadmin oversees both worlds from /admin. Everyone else stays here on
  // the hub — this is their home.
  if (roles.includes('superadmin')) return '/admin';
  return null;   // null = stay put
}

function SignInPanel() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Password is secondary, collapsed. Most people here bought a plan and have
  // no password — but accounts that DO predate Google sign-in were locked out
  // of the front door entirely until this existed.
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const finish = async (user) => {
    const { claims } = await getIdTokenResult(user, true);
    const roles = Array.isArray(claims.roles) ? claims.roles : (claims.role ? [claims.role] : []);
    const to = landingForRoles(roles);
    if (to) { window.location.href = to; return; }
    window.location.reload();
  };

  const withPassword = async () => {
    setError(''); setBusy(true);
    try {
      const { user } = await signInWithEmailAndPassword(auth, email.trim(), password);
      await finish(user);
    } catch (err) {
      console.error('[toolkit] password sign-in failed:', err?.code);
      const code = err?.code || '';
      setError(
        code === 'auth/invalid-credential' || code === 'auth/wrong-password' || code === 'auth/user-not-found'
          ? 'That email and password did not match an account.'
          : code === 'auth/too-many-requests'
            ? 'Too many attempts. Please wait a moment and try again.'
            : "Sign-in didn't work just now. Please try again."
      );
      setBusy(false);
    }
  };

  const continueWithGoogle = async () => {
    setError(''); setBusy(true);
    try {
      const { user } = await signInWithPopup(auth, googleProvider);
      await finish(user);
    } catch (err) {
      console.error('[toolkit] sign-in failed:', err?.code, err?.message);
      const code = err?.code || '';
      setError(
        code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request'
          ? 'Sign-in was cancelled — try again when you\'re ready.'
          : code === 'auth/popup-blocked'
            ? 'Your browser blocked the sign-in popup. Allow popups and try again.'
            : "Sign-in didn't work just now. Please try again."
      );
      setBusy(false);
    }
  };

  return (
    <div className={"w-full max-w-md " + T.PANEL}>
      <div className="p-8">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl border border-gold-soft bg-gold/15">
            <BookOpen className="h-7 w-7 text-gold-deep" />
          </div>
          <h1 className={T.H1_ON_PARCHMENT}>Sign in to Narratoria</h1>
          <p className={"mt-1 " + T.BODY_MUTED}>
            Your Publishing Checklist, Marketing Compass and Story Bible — all in one place.
          </p>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-[#8a3a1e]/30 bg-[#8a3a1e]/10 p-3 text-sm text-[#8a3a1e]">
            {error}
          </div>
        )}

        <button
          type="button"
          className={T.BTN_PRIMARY + " w-full"}
          onClick={continueWithGoogle}
          disabled={busy}
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
          Continue with Google
        </button>

        {!showPassword ? (
          <button
            type="button"
            onClick={() => setShowPassword(true)}
            className={"mx-auto mt-3 flex items-center gap-1 text-sm " + T.BODY_FAINT + " hover:text-[#241f17]"}
          >
            Sign in with a password instead <ChevronDown className="h-3 w-3" />
          </button>
        ) : (
          <div className={"mt-4 space-y-4 p-4 " + T.PANEL_MUTED}>
            <div>
              <Label>Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
            </div>
            <div>
              <Label>Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your password"
                onKeyDown={(e) => e.key === 'Enter' && withPassword()}
              />
            </div>
            <button type="button" className={T.BTN_SECONDARY + " w-full"} onClick={withPassword} disabled={busy || !email || !password}>
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
          </div>
        )}

        <div className="mt-6 rounded-xl border border-gold-soft bg-gold/8 p-4 text-center">
          <p className={T.MICRO + " text-gold-deep"}>New here?</p>
          <p className={"mt-1 leading-relaxed " + T.BODY_MUTED}>
            See what's included and start a free 10-day trial — no card required.
          </p>
          <button
            type="button"
            className={T.BTN_SECONDARY + " mt-3"}
            onClick={() => { window.location.href = '/publishing-roadmap?view=plans'; }}
          >
            View plans <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>

        <p className={"mt-6 text-center text-sm leading-relaxed " + T.BODY_FAINT}>
          Already have a licence key? Enter it on the{' '}
          <a href="/publishing-roadmap" className="font-semibold text-gold-deep hover:text-[#7c5f13]">
            Publishing Checklist
          </a>{' '}
          — it unlocks every tool.
        </p>
      </div>
    </div>
  );
}

/**
 * VERIFY-YOUR-EMAIL PROMPT.
 *
 * `getMyLicence` will only match a licence to an account by email when that
 * email is VERIFIED — matching unverified addresses would let anyone claim a
 * stranger's licence by signing up with it. So an unverified account with a
 * licence waiting on its address sees locked cards and no explanation.
 *
 * Shown ONLY when unverified AND nothing is entitled: an account whose licence
 * is already claimed to its uid works fine unverified, and telling it to
 * "verify to unlock" would be simply untrue.
 *
 * Once verified, the next load auto-claims via getMyLicence step 2 — no support
 * intervention, no key entry.
 */
function VerifyEmailPrompt({ user }) {
  const [state, setState] = useState('idle');   // idle | sending | sent | error

  const resend = async () => {
    setState('sending');
    try { await sendEmailVerification(user); setState('sent'); }
    catch (err) {
      console.error('[toolkit] resend verification failed:', err?.code);
      setState('error');
    }
  };

  return (
    <div className={"mb-4 " + T.PANEL}>
      <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start">
        <MailWarning className="mt-0.5 h-5 w-5 shrink-0 text-gold-deep" />
        <div className="min-w-0 flex-1">
          <p className={"font-cinzel font-bold " + T.BODY}>Verify your email to unlock your tools</p>
          <p className={"mt-1 leading-relaxed " + T.BODY_MUTED}>
            We can only match your licence to a verified address. Check your inbox for the
            link we sent to <strong className="font-semibold">{user?.email}</strong> — then
            reload this page and your tools will be waiting.
          </p>
          {state === 'sent' ? (
            <p className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-[#2f5a34]">
              <Check className="h-3.5 w-3.5" /> Sent — check your inbox and spam folder.
            </p>
          ) : state === 'error' ? (
            <p className="mt-2 text-sm text-rose-700">
              Couldn't send just now.{' '}
              <button onClick={resend} className="font-medium underline">Try again</button>
            </p>
          ) : (
            <button
              type="button"
              className={T.BTN_SECONDARY + ' mt-2'}
              onClick={resend}
              disabled={state === 'sending'}
            >
              {state === 'sending' && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Resend verification email
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ToolkitHome() {
  useDocumentTitle('Your tools');
  const { user, isLoading, isSuperadmin } = useAuth();

  // Keep the sign-out control out of the hub component itself — the hub is also
  // embedded elsewhere, and only this page owns the session.
  const [signingOut, setSigningOut] = useState(false);
  useEffect(() => { setSigningOut(false); }, [user]);

  // null until the hub reports back, so the verify prompt never flashes before
  // we know whether anything is actually locked.
  const [entitled, setEntitled] = useState(null);
  const needsVerification = !!user && user.emailVerified === false && entitled === false;

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-parchment/20 border-t-gold" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className={"flex min-h-screen items-center justify-center p-4 bg-ink " + T.BODY_FONT}>
        <SignInPanel />
      </div>
    );
  }

  return (
    <div className={"min-h-screen bg-ink " + T.BODY_FONT}>
      <div className="mx-auto w-full max-w-5xl px-4 py-8">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className={T.H1}>Your Narratoria tools</h1>
            <p className={"mt-1 " + T.ON_INK}>Everything included with your plan.</p>
          </div>
          <div className="flex items-center gap-2">
            {/* Superadmin's way through to the CRM. Deliberately the ONLY
                cross-world link on this page, and only visible to superadmin. */}
            {isSuperadmin && (
              <button type="button" className={T.BTN_ON_INK} onClick={() => { window.location.href = '/admin'; }}>
                Admin CRM <ArrowRight className="h-3.5 w-3.5" />
              </button>
            )}
            <button
              type="button"
              className={T.BTN_ON_INK}
              disabled={signingOut}
              onClick={async () => {
                setSigningOut(true);
                const { signOut } = await import('firebase/auth');
                try { await signOut(auth); window.location.href = '/'; }
                catch { setSigningOut(false); }
              }}
            >
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </div>

        {needsVerification && <VerifyEmailPrompt user={user} />}

        <AuthorHub onEntitlementResolved={setEntitled} />
      </div>
    </div>
  );
}
